import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router'
import { useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useWorkflows } from '@/api/hooks/useWorkflow'
import { useWorkOrders } from '@/api/hooks/useWorkOrders'
import KanbanHeader from '@/components/molecules/kanban-header'
import { PunchClockBanner } from '@/components/molecules/punch-clock-banner'
import { WorkOrderCard } from '@/components/molecules/work-order-card/work-order-card'
import { WorkOrderSearch } from '@/components/molecules/work-order-search'
import type { KanbanStatusDefinition } from '@/components/organisms/kanban-board'
import { KanbanBoard } from '@/components/organisms/kanban-board'
import { usePageHeaderActions } from '@/contexts/page-header-actions-context'
import { useKanbanFilters } from '@/hooks/useKanbanFilters'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { SettingsActions } from '@/redux/reducers/settings'
import { PermissionsUtils } from '@/utils/permissions.utils'

export const Route = createFileRoute('/_authenticated/handoff')({
  component: HandoffPage,
})

// ─── Domain types ────────────────────────────────────────────────────────────

type Priority = 'low' | 'medium' | 'high'

// ─── Priority config ──────────────────────────────────────────────────────────

export const priorityConfig: Record<Priority, { label: string; className: string }> = {
  low: { label: 'Low', className: 'bg-layer-3 text-text-secondary border-transparent' },
  medium: { label: 'Medium', className: 'bg-warning/10 text-warning border-transparent' },
  high: { label: 'High', className: 'bg-destructive/10 text-destructive border-transparent' },
}

function HandoffPage() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const layout = useAppSelector(state => state.settings.kanbanLayouts.handoff)
  const activeWorkshopId = useAppSelector(state => state.settings.activeWorkshopId)
  const setLayout = (l: 'horizontal' | 'vertical') =>
    dispatch(SettingsActions.setKanbanLayout({ page: 'handoff', layout: l }))
  const { actionsEl } = usePageHeaderActions()
  const { data: workOrders } = useWorkOrders(activeWorkshopId)
  const { data: workflows } = useWorkflows(activeWorkshopId)

  const workOrderStatuses: KanbanStatusDefinition<string>[] = useMemo(() => {
    if (!workflows) return []

    return workflows?.workflowStates
      .filter(state => workflows.handoff.includes(state.id))
      .map(state => ({
        id: state.id,
        title: state.name,
        workflowState: state,
      }))
  }, [workflows])

  const archivedStateIds = useMemo(
    () => workflows?.workflowStates.filter(s => s.type === 'archive').map(s => s.id) ?? [],
    [workflows],
  )

  const filteredWorkOrders = useMemo(() => {
    if (!workOrders || !workflows) return []

    return (
      workOrders
        .filter(wo => wo.currentStates.some(s => workflows.handoff.includes(s.id)))
        // Also filter out work orders in archived states, even if they are included in handoff filters, to avoid showing them on the board
        .filter(wo => !wo.currentStates.some(s => archivedStateIds.includes(s.id)))
    )
  }, [workOrders, workflows])

  const kanbanStatuses = useMemo(() => {
    return workOrderStatuses.filter(s => s.workflowState?.type !== 'archive')
  }, [workOrderStatuses])

  const {
    statuses: visibleStatuses,
    workOrders: visibleWorkOrders,
    getItemStatuses,
  } = useKanbanFilters({
    workflowKey: 'handoff',
    statuses: kanbanStatuses,
    workOrders: filteredWorkOrders,
  })

  return (
    <div className="flex flex-col bg-background h-full w-full">
      {actionsEl &&
        createPortal(
          <KanbanHeader
            layout={layout}
            onLayoutChange={setLayout}
            workflowKey="handoff"
            statuses={kanbanStatuses}
            right={<WorkOrderSearch page="handoff" />}
            left={
              PermissionsUtils.hasPermission('time-entries:create') && (
                <div className="flex max-md:hidden">
                  <PunchClockBanner />
                </div>
              )
            }
          />,
          actionsEl,
        )}
      {PermissionsUtils.hasPermission('time-entries:create') && (
        <div className="px-4 py-2 border-b bg-layer-1 border-border-subtle-1 hidden max-md:flex flex-row-reverse">
          <PunchClockBanner />
        </div>
      )}
      <KanbanBoard
        boardClassName={layout === 'horizontal' ? 'pb-2 pl-2' : undefined}
        getItemId={item => item.id}
        getItemStatuses={getItemStatuses}
        items={visibleWorkOrders}
        layout={layout}
        renderCard={({ item, status, occurrence, layout }) => (
          <WorkOrderCard
            layout={layout}
            currentStateId={status.id}
            lane={item.currentStates.filter(s => s.id === status.id)[occurrence]?.lane ?? 'main'}
            statuses={workOrderStatuses}
            workOrder={item}
            onCardClick={wo =>
              navigate({
                to: '/handoff/workorder/$workOrderId',
                params: { workOrderId: wo.id },
                state: { initialWorkOrder: wo } as never,
              })
            }
          />
        )}
        statuses={visibleStatuses}
      />
      <Outlet />
    </div>
  )
}
